import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { formToObject } from 'lib/forms/formToObject'
import React, { FormEventHandler, useCallback, useContext, useRef } from 'react'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { useMutation } from '@tanstack/react-query'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import { requestPostLinkPersonalAccount } from 'businessTraveller/api/requestPostLinkPersonalAccount'
import requestPatchMyCreditCustomerId from 'businessTraveller/api/requestPatchMyCreditCustomerId'
import { login, LoginOptions } from 'api/auth'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { LUXURY_ESCAPES } from 'constants/brands'
import { BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS, BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS, BusinessTravellerPersonalAccountLinkingDispatchContext, BusinessTravellerPersonalAccountLinkingStateContext } from 'businessTraveller/contexts/BusinessTravellerPersonalAccountLinkingContexts'
import { BUSINESS_TRAVELLER_ACTIONS } from 'reducers/businessTravellerActionReducer'
import { useAppDispatch } from 'hooks/reduxHooks'

interface FormData {
  password: string
}

function BusinessTravellerExistingPersonalUserSetupStep() {
  const {
    businessDetails,
    personalEmail,
  } = useContext(BusinessTravellerPersonalAccountLinkingStateContext)
  const accountLinkingDispatch = useContext(BusinessTravellerPersonalAccountLinkingDispatchContext)
  const dispatch = useAppDispatch()
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const proceed = useCallback(() => {
    if (personalEmail) {
      dispatch({
        type: BUSINESS_TRAVELLER_ACTIONS.UPDATE_CREDIT_CUSTOMER_DETAILS,
        creditCustomerEmail: personalEmail,
      })
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.SET_PERSONAL_EMAIL,
        personalEmail,
      })
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
        step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.PERSONAL_ACCOUNT_LINK_CONFIRMATION,
      })
    }
  }, [accountLinkingDispatch, dispatch, personalEmail])

  const accountLoginMutation = useMutation({
    mutationFn: async(variables: FormData) => {
      if (!personalEmail) {
        return Promise.reject('Personal email was malformed!')
      }
      // Reset so the invisible reCAPTCHA issues a fresh single-use token on retry — otherwise
      // executeAsync can hang on the token a previous failed attempt already consumed.
      recaptchaRef.current?.reset()
      const recaptchaResponse = await handleReCAPTCHA(recaptchaRef.current)
      try {
        if (isDedicatedTenantRegistrationEnabled) {
          // Flag-on: authenticate the personal LE account (no session) to obtain its token, then
          // link via the self-service endpoint which derives the account id from that verified token.
          const loginOptions: LoginOptions = { recaptchaData: recaptchaResponse, brand: LUXURY_ESCAPES, includeCredentials: false }
          const authResult = await login({ email: personalEmail }, variables.password, loginOptions)
          return requestPatchMyCreditCustomerId({ businessId: businessDetails.id, linkedAccountToken: authResult.accessToken })
        }

        const response = await requestPostLinkPersonalAccount({ login: personalEmail, password: variables.password, recaptchaResponse, businessId: businessDetails.id })
        return response
      } catch(err) {
        // login() throws an ApiCallFailure object; requestPatchMyCreditCustomerId rejects with a
        // plain error-message string. Normalise so the mutation error stays truthy either way.
        return Promise.reject(typeof err === 'string' ? err : (err as App.ApiCallFailure).message)
      }
    },
    onSuccess: (data) => {
      if (data) {
        proceed()
      }
    },
  })

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()

    const formData = formToObject<FormData>(event.currentTarget)

    accountLoginMutation.mutate(formData)
  }, [accountLoginMutation])

  const goBack = useCallback(() => {
    accountLinkingDispatch({
      type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
      step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.PERSONAL_EMAIL_UPDATE,
    })
  }, [accountLinkingDispatch])

  const isBusy = accountLoginMutation.isLoading

  return <form onSubmit={handleSubmission}>
    <ModalHeader title="" onBackButtonClick={!isBusy ? goBack : undefined} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={8}>
            <Heading variant="heading4">
              Welcome back
            </Heading>

            <BodyText variant="large">
              Please enter your password below to continue.
            </BodyText>
          </Group>

          <Group direction="vertical" gap={12}>
            <PasswordInput
              name="password"
              label="Password"
              required
              autoFocus
            />

            {!!accountLoginMutation.error && <MessageBanner
              description="Unable to link personal account"
              kind="critical"
            />}

            <TextButton
              type="submit"
              kind="primary"
              size="large"
              disabled={isBusy}
            >
              Continue
            </TextButton>
          </Group>
        </Group>
      </ModalContent>
      {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />}
    </ModalBody>
  </form>
}

export default BusinessTravellerExistingPersonalUserSetupStep
